import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Modal from "../../layouts/Modal";
import { TextInput } from "../shared/Inputs/TextInput";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { IMaterial } from "../../models/Material";
import useMaterials from "../../data/useMaterials";

type FormValues = {
  name: string;
};

interface MaterialsModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  material?: IMaterial;
}

export default function MaterialsModal({ open, setOpen, material }: MaterialsModalProps) {
  const { deleteHandlers, saveHandlers } = useDefaultCRUDHandlers("Material");
  const { update, create, remove } = useMaterials();
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const { register, handleSubmit, reset } = useForm<FormValues>();

  const onSubmit = (data: FormValues) => {
    const updatedMaterial = {
      ...material,
      ...data
    };

    if (material) {
      update.mutate(updatedMaterial as IMaterial, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    } else {
      create.mutate(updatedMaterial, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError
      });
    }
  };

  const handleDelete = () => {
    remove.mutate(material?.id!, {
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setOpen(false);
        setDeleteOpen(false);
        reset({});
      },
      onError: deleteHandlers.onError
    });
  };

  useEffect(() => {
    if (material) {
      reset({
        name: material.name,
      });
    } else {
      reset({});
    }
  }, [material, open, reset]);

  return (
    <>
      <ConfirmModal
        open={deleteOpen}
        setOpen={setDeleteOpen}
        title={"Delete Material"}
        message={`Are you sure you want to delete this material?\n This action cannot be undone.`}
        confirmButtonText={"Delete"}
        onConfirm={handleDelete}
        isLoading={false}
      />
      <Modal width="max-w-xl" open={open} setOpen={setOpen}>
        <h1 className="text-xl">{`${material ? "Edit" : "Add"} Material`}</h1>
        <div className="mt-4">
          <form className="space-y-4 border-t border-gray-200" onSubmit={handleSubmit(onSubmit)}>
            <div className="pt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-1">
              <TextInput label="Name" registerName="name" register={register} />
              <ButtonInput label={`${material ? "Save Changes" : "Create"}`} isSubmit={true} onClick={undefined} classes={undefined} />
              {material !== undefined && (
                <ButtonInput label={"Delete"} isSubmit={false} onClick={() => setDeleteOpen(true)} classes={"!bg-red-500 hover:!bg-red-600"} />
              )}
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
}
